<template>
  <div class="simple-card-view" :class="{ 'in-context-pot': isInContextPot, 'selected': isSelected }" @click="handleCardClick">
    <!-- Batch Selection Checkbox -->
    <div v-if="batchSelectionMode" class="selection-checkbox" @click.stop="toggleSelection">
      <el-checkbox :value="isSelected" @change="toggleSelection"></el-checkbox>
    </div>

    <el-card class="card-container" shadow="hover">
      <div class="card-content">
        <div v-if="card.title" class="card-title">{{ card.title }}</div>
        <div v-if="cardCollections.length > 0 || (card.labels && card.labels.length > 0)" class="card-labels">
          <el-tag
            v-for="collection in cardCollections"
            :key="`collection-${collection.id}`"
            size="mini"
            :color="getCollectionTagColor(collection.name)"
            class="label-tag collection-tag"
          >
            {{ collection.name }}
          </el-tag>
          <el-tag
            v-for="(label, index) in card.labels"
            :key="`label-${index}`"
            size="mini"
            type="info"
            class="label-tag"
          >
            {{ label }}
          </el-tag>
        </div>
        <template v-if="!card.content_type || card.content_type === 'text'">
          <div class="text-content" v-html="renderContent()"></div>
        </template>
        <template v-else-if="card.content_type === 'quote'">
          <figure class="quote-content">
            <div class="quote-mark" aria-hidden="true">&ldquo;</div>
            <blockquote v-html="renderQuoteContent()"></blockquote>
          </figure>
        </template>
        <template v-else-if="isKeyTermsCard(card)">
          <KeyTermsCard :card="card" compact />
        </template>
        <template v-else-if="card.content_type === 'image'">
          <div class="image-content">
            <img v-if="card.fileList && card.fileList.length > 0"
              :src="card.fileList[0].url"
              :alt="'Card Image'"
            >
          </div>
        </template>
        <template v-else-if="card.content_type === 'video'">
          <div class="video-content">
            <video v-if="card.fileList && card.fileList.length > 0" controls>
              <source :src="card.fileList[0].url" :type="card.fileList[0].mime_type">
              Your browser does not support the video tag.
            </video>
          </div>
        </template>
        <template v-else-if="card.content_type === 'quiz'">
          <div class="quiz-content">
            <div class="quiz-question"><strong>Question:</strong> <span>{{ card.quiz_content.question }}</span></div>
            <div v-for="(option, idx) in card.quiz_content.options.filter(option => option !== '')" :key="idx" class="quiz-option">
              <div class="quiz-option-content">
                <div style="display: flex; flex: 1; align-items: flex-start;">
                  <span style="min-width: 30px; line-height: 1.5;">{{ ['A','B','C','D'][idx] }}.</span>
                  <span style="line-height: 1.5;">{{ option }}</span>
                </div>
              </div>
            </div>
            <div v-if="showAnswer" class="quiz-explanation">
              <div class="explanation-header">Explanation:</div>
              <div class="explanation-content">{{ card.quiz_content.explanation }}</div>
            </div>
          </div>
        </template>
        
        <!-- Resources Section -->
        <div v-if="card.resources && card.resources.length > 0" class="card-resources">
          <div class="resources-divider"></div>
          <div class="resources-header">
            <i class="el-icon-notebook-2"></i>
            <span>Resources</span>
          </div>
          <div class="resources-citations">
            <div v-for="(resource, index) in card.resources" :key="index" class="resource-citation">
              <span class="citation-number">[{{ index + 1 }}]</span>
              <span class="citation-content">
                <template v-if="resource.author">{{ resource.author }}. </template>
                <span class="citation-title">{{ resource.name }}</span>
                <template v-if="resource.type">. <em>{{ formatResourceType(resource.type) }}</em></template>
                <template v-if="resource.url">
                  . <a :href="resource.url" target="_blank" rel="noopener noreferrer" class="citation-link">
                    <i class="el-icon-link"></i> Link
                  </a>
                </template>
                <template v-if="resource.description">
                  <span class="citation-description">{{ resource.description }}</span>
                </template>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Reminder Details (hidden by default) -->
      <transition name="fade">
        <div
          v-show="(card.reminder_enabled || card.enable_reminder) && showReminderDetails"
          class="reminder-details-popup"
          @click.stop
        >
          <div class="reminder-header">
            <i class="el-icon-time"></i>
            <span class="reminder-title">Reminder Schedule</span>
          </div>
          <div class="reminder-content">
            <div class="reminder-item">
              <span class="reminder-label">Type:</span>
              <span class="reminder-value">{{ getReminderTypeText() }}</span>
            </div>
            <div class="reminder-item">
              <span class="reminder-label">Time:</span>
              <span class="reminder-value">{{ formatReminderTime() }}</span>
            </div>
            <div v-if="card.reminder_schedule" class="reminder-item">
              <span class="reminder-label">Start:</span>
              <span class="reminder-value">{{ formatReminderDate(card.reminder_schedule.start_date) }}</span>
            </div>
            <div v-if="card.reminder_schedule && card.reminder_schedule.end_date" class="reminder-item">
              <span class="reminder-label">End:</span>
              <span class="reminder-value">{{ formatReminderDate(card.reminder_schedule.end_date) }}</span>
            </div>
            <div class="reminder-item">
              <span class="reminder-label">Next:</span>
              <span class="reminder-value">{{ getNextReminderText() }}</span>
            </div>
          </div>
        </div>
      </transition>
      
      <div class="card-footer">
        <div class="footer-left">
          <span class="card-date">{{ formatDate(card.created_at) }}</span>
          <div
            v-if="showReminder && (card.reminder_enabled || card.enable_reminder)"
            class="reminder-indicator"
            @click.stop="toggleReminderDetails"
          >
            <i class="el-icon-bell"></i>
          </div>
        </div>
        <div v-if="showActions" class="footer-right">
          <!-- Star Button -->
          <div
            class="star-button"
            :class="{ 'starred': card.is_starred }"
            @click.stop="toggleStar"
            :title="card.is_starred ? 'Unstar' : 'Star'"
          >
            <i :class="card.is_starred ? 'el-icon-star-on' : 'el-icon-star-off'"></i>
          </div>
          <div class="more-actions-trigger" @click.stop>
            <i class="el-icon-more"></i>
            <!-- Floating Action Menu -->
            <div class="floating-actions" @click.stop>
              <div class="action-menu">
                <button
                  class="action-item"
                  @click.stop="handleAddToContext"
                >
                  {{ isInContextPot ? 'Remove from Context' : 'Add to Context' }}
                </button>
                <button
                  class="action-item"
                  @click.stop="handleEdit"
                >
                  Edit
                </button>
                <button
                  v-if="canShare"
                  class="action-item"
                  @click.stop="handleShare"
                >
                  Share
                </button>
                <button
                  class="action-item"
                  @click.stop="handleDelete"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { postRequest } from '@/apis/front'
import KeyTermsCard from './KeyTermsCard.vue'

export default {
  name: 'SimpleCardView',
  components: {
    KeyTermsCard
  },
  props: {
    card: {
      type: Object,
      required: true,
      default: () => ({
        id: '',
        title: '',
        content: '',
        content_type: 'text',
        created_at: Date.now(),
        fileList: [],
        labels: [],
        quiz_content: {
          question: '',
          options: [],
          explanation: ''
        },
        reminder_enabled: false,
        reminder_schedule: {
          type: 'weekly',
          custom_days: 7,
          time: null,
          start_date: null,
          end_date: null
        }
      })
    },
    collections: {
      type: Array,
      default: () => []
    },
    showAnswer: {
      type: Boolean,
      default: true
    },
    isInContextPot: {
      type: Boolean,
      default: false
    },
    canShare: {
      type: Boolean,
      default: false
    },
    batchSelectionMode: {
      type: Boolean,
      default: false
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    showActions: {
      type: Boolean,
      default: true
    },
    showReminder: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showReminderDetails: false
    }
  },
  computed: {
    cardCollections() {
      if (!this.collections || this.collections.length === 0) {
        return []
      }

      // Support both old (collection_id) and new (collection_ids) format
      let collectionIds = []
      if (this.card.collection_ids && Array.isArray(this.card.collection_ids)) {
        collectionIds = this.card.collection_ids
      } else if (this.card.collection_id) {
        collectionIds = [this.card.collection_id]
      }

      if (collectionIds.length === 0) {
        return []
      }

      return this.collections.filter(col => collectionIds.includes(col.id))
    },
    getCollectionTagColor() {
      return (collectionName) => {
        // Generate a consistent color based on collection name
        const colors = [
          '#409eff', // blue
          '#67c23a', // green
          '#e6a23c', // orange
          '#f56c6c', // red
          '#9c27b0', // purple
          '#00bcd4', // cyan
          '#ff9800', // amber
          '#4caf50', // green
        ]
        const hash = collectionName.split('').reduce((acc, char) => {
          return char.charCodeAt(0) + ((acc << 5) - acc)
        }, 0)
        return colors[Math.abs(hash) % colors.length]
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    isKeyTermsCard(card) {
      const contentType = (card && card.content_type ? card.content_type : '').toString().toLowerCase()
      return ['key_terms', 'keyterms', 'key-terms', 'key_terms_card', 'keyterms_card', 'key-terms-card', 'terms', 'term_list'].includes(contentType)
    },
    handleClickOutside(event) {
      if (this.showReminderDetails && 
          !this.$el.querySelector('.reminder-indicator')?.contains(event.target) &&
          !this.$el.querySelector('.reminder-details-popup')?.contains(event.target)) {
        this.showReminderDetails = false
      }
    },
    getQuoteText() {
      return (
        this.card.content ||
        this.card.quote_content ||
        this.card.quote ||
        this.card.text ||
        ''
      ).trim()
    },
    renderQuoteContent() {
      const quote = this.getQuoteText()
      if (!quote) {
        return '<p class="quote-placeholder">No quote is available for this card yet.</p>'
      }

      marked.setOptions({
        breaks: true,
        gfm: true,
        headerIds: false,
        mangle: false
      })
      return DOMPurify.sanitize(marked(quote), {
        ADD_TAGS: ['mark'],
        ADD_ATTR: ['target', 'rel']
      })
    },
    renderContent() {
      if (!this.card.content) return ''
      
      // Check the editor type
      const editorType = this.card.editor_type || 'auto'
      
      // If editor type is explicitly set, use it
      if (editorType === 'markdown') {
        // Configure marked for better rendering
        marked.setOptions({
          breaks: true, // Enable line breaks
          gfm: true, // GitHub Flavored Markdown
          headerIds: false,
          mangle: false
        })
        const rawHtml = marked(this.card.content)
        return DOMPurify.sanitize(rawHtml, {
          ADD_TAGS: ['mark'], // Allow highlight tags
          ADD_ATTR: ['target', 'rel'] // Allow link attributes
        })
      } else if (editorType === 'richtext') {
        // Rich text is already HTML, just sanitize it
        return DOMPurify.sanitize(this.card.content)
      } else {
        // Auto-detect markdown patterns
        const content = this.card.content
        const hasMarkdownPatterns = this.detectMarkdownPatterns(content)
        
        if (hasMarkdownPatterns || editorType === 'auto') {
          // Try to render as markdown
          try {
            marked.setOptions({
              breaks: true,
              gfm: true,
              headerIds: false,
              mangle: false
            })
            const rawHtml = marked(content)
            // Only use markdown if it actually changed the content
            if (rawHtml !== `<p>${content}</p>\n` && rawHtml !== content) {
              return DOMPurify.sanitize(rawHtml, {
                ADD_TAGS: ['mark'],
                ADD_ATTR: ['target', 'rel']
              })
            }
          } catch (e) {
            console.warn('Markdown parsing failed, falling back to plain text', e)
          }
        }
        
        // Fall back to plain text with line breaks
        const escaped = content
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
          .replace(/\n/g, '<br>')
        return escaped
      }
    },
    
    detectMarkdownPatterns(text) {
      if (!text) return false
      
      // Common markdown patterns to detect
      const patterns = [
        /^#{1,6}\s+/m, // Headers
        /\*\*[^*]+\*\*/,  // Bold
        /\*[^*\n]+\*/,    // Italic  
        /\[([^\]]+)\]\(([^)]+)\)/, // Links
        /^[\*\-\+]\s+/m,  // Unordered lists
        /^\d+\.\s+/m,     // Ordered lists
        /^>\s+/m,         // Blockquotes
        /`[^`]+`/,        // Inline code
        /```[\s\S]*?```/, // Code blocks
        /!\[([^\]]*)\]\(([^)]+)\)/, // Images
        /^---+$/m,        // Horizontal rules
        /\~\~[^~]+\~\~/,  // Strikethrough
        /\^\^[^^]+\^\^/,  // Superscript
        /==[^=]+==/       // Highlight
      ]
      
      return patterns.some(pattern => pattern.test(text))
    },
    handleCardClick(event) {
      console.log('[SimpleCardView] handleCardClick triggered', event.target)
      // Check if click originated from action menu or star button
      const target = event.target
      if (target && target.closest) {
        const isMoreActions = target.closest('.more-actions-trigger')
        const isFloatingActions = target.closest('.floating-actions')
        const isActionMenu = target.closest('.action-menu')
        const isActionItem = target.closest('.action-item')
        const isStarButton = target.closest('.star-button')
        const isMoreIcon = target.classList.contains('el-icon-more')

        console.log('[SimpleCardView] Click checks:', {
          isMoreActions, isFloatingActions, isActionMenu, isActionItem, isStarButton, isMoreIcon
        })

        if (isMoreActions || isFloatingActions || isActionMenu || isActionItem || isStarButton || isMoreIcon) {
          console.log('[SimpleCardView] Click on action menu detected, ignoring')
          event.stopPropagation()
          event.preventDefault()
          return
        }
      }
      console.log('[SimpleCardView] Emitting click event with card:', this.card)
      this.$emit('click', this.card)
    },
    toggleSelection() {
      this.$emit('toggle-selection', this.card.id)
    },
    handleEdit() {
      this.$emit('edit', this.card)
    },
    async handleDelete() {
      console.log('SimpleCardView handleDelete called with card:', this.card);
      
      // Validate card data
      if (!this.card || !this.card.id) {
        console.error('Invalid card object for deletion:', this.card);
        this.$message.error('Cannot delete: Invalid card data');
        return;
      }
      
      // Show confirmation dialog
      this.$confirm('Are you sure you want to delete this card?', 'Confirm Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(async () => {
        console.log('User confirmed deletion for card ID:', this.card.id);
        
        // Show loading state
        const loading = this.$loading({
          lock: true,
          text: 'Deleting card...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        });
        
        try {
          // Make delete API call
          const response = await postRequest('deleteCard', {
            user_id: this.$store.state.user_id,
            card_id: this.card.id
          });

          if (response.data.code === 0) {
            loading.close();
            this.$message.success('Card deleted successfully');
            // Emit success event to parent to update the list
            this.$emit('delete-success', this.card.id);
          } else {
            loading.close();
            this.$message.error(response.data.msg || 'Failed to delete card');
          }
        } catch (error) {
          loading.close();
          console.error('Error deleting card:', error);
          this.$message.error('Failed to delete card');
        }
      }).catch(() => {
        console.log('User cancelled deletion');
        // User cancelled, no need to show message
      });
    },
    handleAddToContext() {
      this.$emit('add-to-context', this.card)
    },
    handleShare() {
      this.$emit('share', this.card)
    },
    toggleStar() {
      this.$emit('toggle-star', this.card)
    },
    formatDate(timestamp) {
      // Handle both Unix timestamps (seconds) and JavaScript timestamps (milliseconds)
      // If timestamp is less than 10000000000, it's likely in seconds
      const date = timestamp < 10000000000 ? new Date(timestamp * 1000) : new Date(timestamp)
      const now = new Date()
      const diff = now - date
      const seconds = Math.floor(diff / 1000)
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      const days = Math.floor(hours / 24)
      
      if (days > 7) {
        return date.toLocaleDateString()
      } else if (days > 0) {
        return `${days} day${days > 1 ? 's' : ''} ago`
      } else if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`
      } else if (minutes > 0) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
      } else {
        return 'Just now'
      }
    },
    
    toggleReminderDetails() {
      this.showReminderDetails = !this.showReminderDetails
    },
    
    getReminderTypeText() {
      const schedule = this.card.reminder_schedule || this.card.recall_schedule
      if (!schedule) return ''
      
      // If using recall_schedule format, determine type based on times
      if (this.card.recall_schedule) {
        return 'Spaced Repetition'
      }
      
      switch (schedule.type) {
        case 'daily':
          return 'Daily'
        case 'weekly':
          return 'Weekly'
        case 'monthly':
          return 'Monthly'
        case 'custom':
          return `Every ${schedule.custom_days} days`
        default:
          return 'Weekly'
      }
    },
    
    formatReminderTime() {
      const schedule = this.card.reminder_schedule || this.card.recall_schedule
      
      if (this.card.recall_schedule) {
        // For recall_schedule, show next recall time
        const now = Date.now() / 1000
        if (now < this.card.recall_schedule.three_days_time) {
          return 'In 3 days'
        } else if (now < this.card.recall_schedule.week_time) {
          return 'In 1 week'
        } else if (now < this.card.recall_schedule.month_time) {
          return 'In 1 month'
        }
        return 'Review cycle'
      }
      
      if (!schedule || !schedule.time) return 'Not set'
      
      const time = new Date(schedule.time)
      return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    
    formatReminderDate(timestamp) {
      if (!timestamp) return 'Not set'
      
      const date = new Date(timestamp)
      return date.toLocaleDateString()
    },
    
    getNextReminderText() {
      // Handle recall_schedule format
      if (this.card.recall_schedule) {
        const now = Date.now() / 1000 // Convert to seconds
        const schedule = this.card.recall_schedule
        
        let nextTime = null
        let label = ''
        
        if (now < schedule.three_days_time) {
          nextTime = schedule.three_days_time
          label = '3 days'
        } else if (now < schedule.week_time) {
          nextTime = schedule.week_time
          label = '1 week'
        } else if (now < schedule.month_time) {
          nextTime = schedule.month_time
          label = '1 month'
        } else if (schedule.three_months_time && now < schedule.three_months_time) {
          nextTime = schedule.three_months_time
          label = '3 months'
        } else {
          return 'Review completed'
        }
        
        if (nextTime) {
          const nextDate = new Date(nextTime * 1000)
          const diffTime = nextDate - new Date()
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          
          if (diffDays === 0) {
            return `Today (${label})`
          } else if (diffDays === 1) {
            return `Tomorrow (${label})`
          } else if (diffDays <= 7) {
            return `In ${diffDays} days (${label})`
          } else {
            return `${nextDate.toLocaleDateString()} (${label})`
          }
        }
      }
      
      // Handle reminder_schedule format
      if (!this.card.reminder_schedule || !this.card.reminder_schedule.start_date) {
        return 'Not scheduled'
      }
      
      const now = new Date()
      const startDate = new Date(this.card.reminder_schedule.start_date)
      const reminderTime = this.card.reminder_schedule.time ? new Date(this.card.reminder_schedule.time) : new Date()
      
      // Calculate next reminder based on type
      let nextReminder = new Date(startDate)
      nextReminder.setHours(reminderTime.getHours(), reminderTime.getMinutes(), 0, 0)
      
      // If the start time has passed today, calculate the next occurrence
      while (nextReminder <= now) {
        switch (this.card.reminder_schedule.type) {
          case 'daily':
            nextReminder.setDate(nextReminder.getDate() + 1)
            break
          case 'weekly':
            nextReminder.setDate(nextReminder.getDate() + 7)
            break
          case 'monthly':
            nextReminder.setMonth(nextReminder.getMonth() + 1)
            break
          case 'custom':
            nextReminder.setDate(nextReminder.getDate() + (this.card.reminder_schedule.custom_days || 7))
            break
          default:
            nextReminder.setDate(nextReminder.getDate() + 7)
        }
      }
      
      // Check if end date has passed
      if (this.card.reminder_schedule.end_date && nextReminder > new Date(this.card.reminder_schedule.end_date)) {
        return 'Ended'
      }
      
      const diffTime = nextReminder - now
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays === 0) {
        return 'Today'
      } else if (diffDays === 1) {
        return 'Tomorrow'
      } else if (diffDays <= 7) {
        return `In ${diffDays} days`
      } else {
        return nextReminder.toLocaleDateString()
      }
    },
    
    formatResourceType(type) {
      const typeMap = {
        'book': 'Book',
        'article': 'Article',
        'video': 'Video',
        'website': 'Website',
        'course': 'Course',
        'paper': 'Research Paper',
        'other': 'Resource'
      }
      return typeMap[type] || 'Resource'
    }
  }
}
</script>

<style scoped>
.simple-card-view {
  width: 100%;
  height: auto;
  min-height: 200px;
  position: relative;
}

.simple-card-view.selected .card-container {
  border: 2px solid #409eff;
  box-shadow: 0 0 10px rgba(64, 158, 255, 0.3);
}

.selection-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  background: white;
  border-radius: 4px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.card-container {
  width: 100%;
  height: 100%;
  aspect-ratio: 0.618; /* Golden ratio portrait (1:1.618) */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease;
  position: relative;
}

:deep(.card-container.el-card .el-card__body) {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
}

.card-container:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.card-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  position: relative;
  min-height: 0;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  line-height: 1.4;
  word-wrap: normal;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
}

.card-labels {
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.label-tag {
  font-size: 10px;
  height: 20px;
  line-height: 18px;
}

.collection-tag {
  color: white !important;
  border-color: transparent !important;
  font-weight: 500;
}

.text-content {
  font-size: 14px;
  line-height: 1.6;
  color: #303133;
  word-wrap: break-word;
}

/* Markdown content styling */
.text-content :deep(h1),
.text-content :deep(h2),
.text-content :deep(h3),
.text-content :deep(h4),
.text-content :deep(h5),
.text-content :deep(h6) {
  margin-top: 16px;
  margin-bottom: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: #1a1a1a;
}

.text-content :deep(h1) { font-size: 24px; }
.text-content :deep(h2) { font-size: 20px; }
.text-content :deep(h3) { font-size: 18px; }
.text-content :deep(h4) { font-size: 16px; }
.text-content :deep(h5) { font-size: 14px; }
.text-content :deep(h6) { font-size: 13px; }

.text-content :deep(h1:first-child),
.text-content :deep(h2:first-child),
.text-content :deep(h3:first-child),
.text-content :deep(h4:first-child),
.text-content :deep(h5:first-child),
.text-content :deep(h6:first-child) {
  margin-top: 0;
}

.text-content :deep(p) {
  margin: 0 0 12px 0;
}

.text-content :deep(p:last-child) {
  margin-bottom: 0;
}

.text-content :deep(strong) {
  font-weight: 600;
  color: #1a1a1a;
}

.text-content :deep(em) {
  font-style: italic;
}

.text-content :deep(a) {
  color: #409eff;
  text-decoration: none;
  transition: color 0.2s;
}

.text-content :deep(a:hover) {
  color: #66b1ff;
  text-decoration: underline;
}

.text-content :deep(ul),
.text-content :deep(ol) {
  margin: 12px 0;
  padding-left: 24px;
}

.text-content :deep(ul li),
.text-content :deep(ol li) {
  margin: 6px 0;
  line-height: 1.6;
}

.text-content :deep(ul ul),
.text-content :deep(ol ol),
.text-content :deep(ul ol),
.text-content :deep(ol ul) {
  margin: 4px 0;
}

.text-content :deep(blockquote) {
  margin: 12px 0;
  padding: 8px 16px;
  border-left: 4px solid #409eff;
  background-color: #f5f7fa;
  color: #606266;
}

.text-content :deep(blockquote p) {
  margin: 4px 0;
}

.text-content :deep(code) {
  padding: 2px 6px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  background-color: #f0f2f5;
  color: #e6428b;
  border-radius: 3px;
}

.text-content :deep(pre) {
  margin: 12px 0;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  overflow-x: auto;
}

.text-content :deep(pre code) {
  padding: 0;
  background-color: transparent;
  color: #303133;
}

.text-content :deep(hr) {
  margin: 16px 0;
  border: none;
  border-top: 1px solid #e4e7ed;
}

.text-content :deep(table) {
  width: 100%;
  margin: 12px 0;
  border-collapse: collapse;
  font-size: 13px;
}

.text-content :deep(table th),
.text-content :deep(table td) {
  padding: 8px 12px;
  border: 1px solid #e4e7ed;
  text-align: left;
}

.text-content :deep(table th) {
  background-color: #f5f7fa;
  font-weight: 600;
}

.text-content :deep(table tr:nth-child(even)) {
  background-color: #fafafa;
}

.text-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 8px 0;
}

.text-content :deep(del) {
  text-decoration: line-through;
  color: #909399;
}

.text-content :deep(mark) {
  background-color: #fff3cd;
  color: #1a1a1a;
  padding: 2px 4px;
  border-radius: 2px;
}

.quote-content {
  margin: 0;
  min-height: 180px;
  padding: 24px 22px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: linear-gradient(180deg, #ffffff 0%, #f7f9fc 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.quote-mark {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 60px;
  line-height: 0.8;
  color: #d6dde8;
  margin-bottom: 8px;
}

.quote-content blockquote {
  margin: 0;
  color: #1d2939;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 22px;
  line-height: 1.45;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.quote-content :deep(p) {
  margin: 0 0 10px;
}

.quote-content :deep(p:last-child) {
  margin-bottom: 0;
}

.quote-content :deep(.quote-placeholder) {
  color: #667085;
  font-family: inherit;
  font-size: 15px;
  font-weight: 400;
}

.image-content img {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: cover;
  border-radius: 4px;
}

.video-content video {
  width: 100%;
  height: auto;
  max-height: 200px;
  border-radius: 4px;
}

.quiz-content {
  padding: 10px 0;
}

.quiz-question {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 15px;
  font-weight: 500;
}

.quiz-option {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 8px;
  transition: all 0.3s ease;
  background-color: #FFFFFF;
}

.quiz-option:hover {
  background-color: #F5F7FA;
}

.quiz-option-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.quiz-explanation {
  margin-top: 15px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 4px solid #409eff;
}

.explanation-header {
  font-weight: bold;
  color: #409eff;
  margin-bottom: 6px;
  font-size: 12px;
}

.explanation-content {
  font-size: 12px;
  line-height: 1.4;
  color: #606266;
}

.card-date {
  color: #909399;
  font-size: 12px;
  flex-shrink: 0;
}

.card-footer {
  padding: 8px 20px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #FAFAFA;
  border-top: 1px solid #EBEEF5;
  margin-top: auto;
  position: relative;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 5;
}

.footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  z-index: 5;
}

.star-button {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  transition: all 0.2s;
  color: #909399;
}

.star-button:hover {
  background: #f5f7fa;
  color: #F7BA2A;
}

.star-button.starred {
  color: #F7BA2A;
}

.star-button i {
  font-size: 18px;
}

.more-actions-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: transparent;
  color: #909399;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.more-actions-trigger:hover {
  background: #f0f0f0;
  color: #409EFF;
}

.more-actions-trigger i {
  font-size: 16px;
}

.reminder-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #409EFF, #66B1FF);
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  animation: pulse 2s infinite;
  z-index: 100;
}

.reminder-indicator:hover {
  transform: scale(1.1);
  box-shadow: 0 3px 12px rgba(64, 158, 255, 0.4);
}

.reminder-indicator i {
  font-size: 12px;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(64, 158, 255, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(64, 158, 255, 0);
  }
}

.more-actions-trigger .floating-actions {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: -4px;
  padding-bottom: 12px;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.more-actions-trigger:hover .floating-actions,
.floating-actions:hover {
  opacity: 1;
  pointer-events: auto;
}

.action-menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 4px;
  border: 1px solid #e0e0e0;
}

.action-item {
  padding: 8px 12px;
  font-size: 13px;
  color: #333;
  text-align: left;
  cursor: pointer;
  border: none;
  background: transparent;
  border-radius: 2px;
  transition: background-color 0.15s ease;
  font-family: inherit;
  line-height: 1.4;
  white-space: nowrap;
}

.action-item:hover {
  background-color: #f5f5f5;
}

.action-item:active {
  background-color: #ebebeb;
}

/* Responsive styles */
@media (max-width: 768px) {
  .card-content {
    padding: 15px;
  }
  
  .card-footer {
    padding: 8px 15px;
  }
  
  .reminder-indicator {
    width: 20px;
    height: 20px;
  }
  
  .reminder-indicator i {
    font-size: 10px;
  }
  
  .reminder-details-popup {
    left: 10px;
    right: 10px;
    max-width: calc(100% - 20px);
  }
  
  .action-menu {
    padding: 3px;
  }
  
  .action-item {
    padding: 6px 10px;
    font-size: 12px;
  }
  
  .text-content {
    font-size: 13px;
  }
  
  .quiz-question {
    font-size: 13px;
  }
  
  .quiz-option-content {
    font-size: 11px;
  }
  
  .card-date {
    font-size: 10px;
  }
}

/* Reminder Details Popup Styles */
.reminder-details-popup {
  position: absolute;
  bottom: 45px;
  left: 50px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 250px;
  max-width: 300px;
  overflow: hidden;
  border: 1px solid #E4E7ED;
}

.reminder-header {
  display: flex;
  align-items: center;
  padding: 12px 16px 8px;
  border-bottom: 1px solid #E4E7ED;
  background: linear-gradient(135deg, #f0f7ff, #f8fbff);
}

.reminder-header i {
  color: #409EFF;
  margin-right: 8px;
  font-size: 16px;
}

.reminder-title {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
}

.reminder-content {
  padding: 12px 16px;
  background: white;
}

.reminder-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  padding: 4px 0;
}

.reminder-item:last-child {
  margin-bottom: 0;
}

.reminder-label {
  color: #909399;
  font-weight: 500;
  min-width: 50px;
  display: flex;
  align-items: center;
}

.reminder-label::after {
  content: ':';
  margin-left: 2px;
}

.reminder-value {
  color: #303133;
  text-align: right;
  flex: 1;
  font-weight: 500;
}

/* Fade Animation */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Popup arrow */
.reminder-details-popup::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 30px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid white;
  filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.1));
}

/* Context Pot Highlight Styles */
.simple-card-view.in-context-pot .card-container {
  border: 2px solid #409eff;
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.25);
  background: linear-gradient(135deg, rgba(64, 158, 255, 0.03) 0%, rgba(64, 158, 255, 0.06) 100%);
  position: relative;
}

.simple-card-view.in-context-pot .card-container::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #409eff, #66b1ff, #409eff);
  border-radius: 8px;
  opacity: 0;
  z-index: -1;
  animation: glow 2s ease-in-out infinite;
}

@keyframes glow {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 0.3;
  }
}

.simple-card-view.in-context-pot .card-container:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(64, 158, 255, 0.35);
}

/* Add indicator badge */
.simple-card-view.in-context-pot .card-container::after {
  content: '✓ In Context';
  position: absolute;
  top: 10px;
  right: 10px;
  background: #409eff;
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
}

/* Resources Section Styles */
.card-resources {
  margin-top: 15px;
  padding: 0 20px 15px;
}

.resources-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, #e4e7ed, transparent);
  margin-bottom: 12px;
}

.card-resources .resources-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 500;
  color: #606266;
}

.card-resources .resources-header i {
  font-size: 14px;
  color: #909399;
}

.resources-citations {
  font-size: 12px;
  line-height: 1.6;
  color: #606266;
}

.resource-citation {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  padding-left: 10px;
}

.citation-number {
  min-width: 24px;
  color: #909399;
  font-weight: 500;
  margin-right: 6px;
}

.citation-content {
  flex: 1;
  word-wrap: break-word;
}

.citation-title {
  font-weight: 500;
  color: #303133;
}

.citation-content em {
  font-style: italic;
  color: #909399;
}

.citation-link {
  color: #409eff;
  text-decoration: none;
  font-size: 11px;
  margin-left: 4px;
  transition: color 0.2s;
}

.citation-link:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.citation-link i {
  font-size: 11px;
  margin-right: 2px;
}

.citation-description {
  display: block;
  margin-top: 4px;
  color: #909399;
  font-size: 11px;
  line-height: 1.4;
  padding-left: 24px;
}
</style>
